import { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import MobileNavbar from '../components/MobileNavbar';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  RefreshCw, Activity, Database, GitMerge, Zap, Clock, Calendar,
  CheckCircle2, XCircle, AlertTriangle, Server, Cpu, ShieldAlert, Trash2, KeyRound,
  Mail, Download, Save,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import StepUpDialog from '../components/StepUpDialog';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_MS = 15000;

const formatUptime = (seconds) => {
  if (seconds == null) return '—';
  const s = Math.floor(seconds);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const parts = [];
  if (d) parts.push(`${d}d`);
  if (h) parts.push(`${h}h`);
  if (m || (!d && !h)) parts.push(`${m}m`);
  return parts.join(' ');
};

const StatBadge = ({ ok, okLabel = 'Healthy', badLabel = 'Attention' }) => (
  <Badge
    className={ok
      ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
      : 'bg-amber-100 text-amber-800 border-amber-200'}
    data-testid={`stat-badge-${ok ? 'ok' : 'warn'}`}
  >
    {ok ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <AlertTriangle className="w-3 h-3 mr-1" />}
    {ok ? okLabel : badLabel}
  </Badge>
);

const SectionCard = ({ title, icon: Icon, children, right, testId }) => (
  <Card className="p-5" data-testid={testId}>
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
          <Icon className="w-4 h-4 text-slate-700" />
        </div>
        <h2 className="font-semibold text-slate-800">{title}</h2>
      </div>
      {right}
    </div>
    {children}
  </Card>
);

const KeyRow = ({ label, value, mono = false, testId }) => (
  <div className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-b-0" data-testid={testId}>
    <span className="text-xs text-slate-500">{label}</span>
    <span className={`text-sm font-semibold text-slate-800 ${mono ? 'font-mono' : ''}`}>
      {value}
    </span>
  </div>
);

const SystemHealthPage = () => {
  const { user: me } = useAuth();
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const intervalRef = useRef(null);

  // Super Admin management state
  const [superAdmins, setSuperAdmins] = useState([]);
  const [resetDialog, setResetDialog] = useState({ open: false, user: null, newPwd: '', confirmPwd: '', busy: false });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, user: null, busy: false });

  // Backup state
  const [backupCfg, setBackupCfg] = useState(null);
  const [backupSaving, setBackupSaving] = useState(false);
  const [backupSending, setBackupSending] = useState(false);
  const [backupEmailsInput, setBackupEmailsInput] = useState('');

  const fetchBackupCfg = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/system/backup/config`);
      setBackupCfg(res.data);
      setBackupEmailsInput((res.data.emails || []).join(', '));
    } catch (err) {
      console.warn('backup cfg load failed', err);
    }
  }, []);

  const saveBackupCfg = async () => {
    setBackupSaving(true);
    try {
      const emails = backupEmailsInput.split(',').map((e) => e.trim()).filter(Boolean);
      const body = {
        enabled: backupCfg.enabled,
        emails,
        dayOfWeek: backupCfg.dayOfWeek || 'monday',
        hourUtc: Number(backupCfg.hourUtc ?? 6),
        includeBanks: backupCfg.includeBanks !== false,
        includeVendors: backupCfg.includeVendors !== false,
        includeCompanies: backupCfg.includeCompanies !== false,
        includeSettings: backupCfg.includeSettings !== false,
      };
      const res = await axios.put(`${API}/system/backup/config`, body);
      setBackupCfg(res.data);
      toast.success('Backup settings saved.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setBackupSaving(false);
    }
  };

  const runBackupNow = async () => {
    setBackupSending(true);
    try {
      const res = await axios.post(`${API}/system/backup/run-now`);
      toast.success(res.data.message || 'Backup email sent.');
      fetchBackupCfg();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Backup send failed');
    } finally {
      setBackupSending(false);
    }
  };

  const downloadActivityLogsCsv = async () => {
    try {
      const res = await axios.get(`${API}/activity-logs/export/csv`);
      const blob = new Blob([res.data.csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = res.data.filename || 'activity_logs.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Activity logs downloaded.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Download failed');
    }
  };

  const fetchSuperAdmins = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/system/superadmins`);
      setSuperAdmins(res.data || []);
    } catch (err) {
      // Non-fatal; show a warning only
      console.warn('Failed to load super admins', err);
    }
  }, []);

  const openResetDialog = (u) => setResetDialog({ open: true, user: u, newPwd: '', confirmPwd: '', busy: false });
  const openDeleteDialog = (u) => setDeleteDialog({ open: true, user: u, busy: false });

  const submitReset = async (requestId, code) => {
    await axios.post(`${API}/system/superadmins/${resetDialog.user.id}/reset-password`, { newPassword: resetDialog.newPwd, requestId, code });
    toast.success(`Password reset for '${resetDialog.user.username}'. Their sessions were signed out.`);
    fetchSuperAdmins();
  };

  const submitDelete = async (requestId, code) => {
    await axios.delete(`${API}/system/superadmins/${deleteDialog.user.id}`, { data: { requestId, code } });
    toast.success(`Super Admin '${deleteDialog.user.username}' removed.`);
    fetchSuperAdmins();
  };

  const fetchHealth = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(`${API}/system/health`);
      setHealth(res.data);
      setLastFetchedAt(new Date());
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to fetch system health';
      if (!silent) toast.error(msg);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchHealth(); fetchSuperAdmins(); fetchBackupCfg(); }, [fetchHealth, fetchSuperAdmins, fetchBackupCfg]);

  useEffect(() => {
    if (!autoRefresh) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    intervalRef.current = setInterval(() => fetchHealth(true), REFRESH_MS);
    return () => intervalRef.current && clearInterval(intervalRef.current);
  }, [autoRefresh, fetchHealth]);

  const mirrorInSync = health?.mirror?.inSync;
  const cacheOk = health?.cache?.enabled && health?.cache?.reachable;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <MobileNavbar />
      <div className="max-w-6xl mx-auto px-4 py-8 pb-24 md:pb-8" data-testid="system-health-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Activity className="w-6 h-6 text-emerald-600" />
              System Health
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Real-time platform metrics: database, cache, mirror sync and scheduled jobs.
              {lastFetchedAt && (
                <span className="ml-2 text-slate-400" data-testid="last-fetched">
                  · Updated {lastFetchedAt.toLocaleTimeString()}
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer select-none" data-testid="auto-refresh-toggle">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300"
              />
              Auto-refresh (15s)
            </label>
            <Button
              onClick={() => fetchHealth(false)}
              disabled={loading}
              size="sm"
              variant="outline"
              className="gap-2"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {!health && (
          <Card className="p-12 text-center text-slate-500" data-testid="health-loading">
            {loading ? 'Loading system metrics…' : 'No metrics available.'}
          </Card>
        )}

        {health && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Uptime + Runtime */}
            <SectionCard title="Uptime & Runtime" icon={Server} testId="card-runtime">
              <div className="space-y-0">
                <KeyRow label="Uptime" value={formatUptime(health.uptimeSeconds)} testId="row-uptime" />
                <KeyRow label="Started at" value={health.startedAt ? new Date(health.startedAt).toLocaleString() : '—'} testId="row-started" />
                <KeyRow label="Python" value={health.runtime?.python || '?'} mono testId="row-python" />
                <KeyRow label="Platform" value={health.runtime?.platform || '?'} testId="row-platform" />
                <KeyRow label="MongoDB" value={health.runtime?.mongodb || '?'} mono testId="row-mongo" />
              </div>
            </SectionCard>

            {/* Sample query latency */}
            <SectionCard title="Sample Query Latency" icon={Zap} testId="card-latency">
              <div className="text-center py-4">
                <div className="text-4xl font-bold text-blue-700" data-testid="banks-count-ms">
                  {health.sampleQueries?.banksCountMs ?? '—'}
                  <span className="text-lg text-slate-400 ml-1">ms</span>
                </div>
                <div className="text-xs text-slate-500 mt-2">
                  <code className="bg-slate-100 px-2 py-0.5 rounded">db.banks.count_documents({'{}'})</code>
                </div>
                <div className="text-xs text-slate-400 mt-3">
                  Should stay under 50 ms even at 1M banks. If this climbs, check MongoDB indexes.
                </div>
              </div>
            </SectionCard>

            {/* Collection counts */}
            <SectionCard title="Collection Counts" icon={Database} testId="card-collections">
              <div className="space-y-0">
                {Object.entries(health.collections || {}).map(([name, count]) => (
                  <KeyRow
                    key={name}
                    label={name}
                    value={count === -1 ? <span className="text-red-600">error</span> : count.toLocaleString()}
                    testId={`row-collection-${name}`}
                  />
                ))}
              </div>
            </SectionCard>

            {/* Mirror sync status */}
            <SectionCard
              title="Mirror Sync"
              icon={GitMerge}
              right={<StatBadge ok={mirrorInSync} okLabel="In sync" badLabel={`Lag ${Math.abs(health.mirror?.lag || 0)}`} />}
              testId="card-mirror"
            >
              <div className="space-y-0">
                <KeyRow label="Embedded banks (vendors.banks[])" value={(health.mirror?.embeddedBanksTotal ?? 0).toLocaleString()} testId="row-embedded" />
                <KeyRow label="Mirrored banks (banks collection)" value={(health.mirror?.mirroredBanksTotal ?? 0).toLocaleString()} testId="row-mirrored" />
                <KeyRow
                  label="Lag"
                  value={
                    mirrorInSync
                      ? <span className="text-emerald-700">0 (in sync)</span>
                      : <span className="text-amber-700">{health.mirror?.lag}</span>
                  }
                  testId="row-lag"
                />
              </div>
              {!mirrorInSync && (
                <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                  Mirror lag detected. Restart backend to trigger a fresh <code>sync_banks_collection()</code> pass.
                </div>
              )}
            </SectionCard>

            {/* Cache health */}
            <SectionCard
              title="Upstash Redis Cache"
              icon={Cpu}
              right={
                health.cache?.enabled
                  ? <StatBadge ok={health.cache?.reachable} okLabel="Reachable" badLabel="Unreachable" />
                  : <Badge className="bg-slate-100 text-slate-700 border-slate-200">Disabled</Badge>
              }
              testId="card-cache"
            >
              <div className="space-y-0">
                <KeyRow label="Enabled" value={health.cache?.enabled ? 'yes' : 'no'} testId="row-cache-enabled" />
                <KeyRow label="Reachable" value={health.cache?.reachable ? 'yes' : 'no'} testId="row-cache-reachable" />
                <KeyRow label="Total keys" value={(health.cache?.keyCount ?? 0).toLocaleString()} testId="row-cache-keys" />
              </div>
              {!health.cache?.enabled && (
                <div className="mt-3 p-2 bg-slate-50 border border-slate-200 rounded text-xs text-slate-600">
                  Set <code>UPSTASH_REDIS_REST_URL</code> and <code>UPSTASH_REDIS_REST_TOKEN</code> in backend/.env to enable KPI caching.
                </div>
              )}
            </SectionCard>

            {/* Scheduled reports */}
            <SectionCard
              title="Scheduled Reports"
              icon={Calendar}
              right={
                <Badge className="bg-blue-100 text-blue-800 border-blue-200">
                  {health.schedules?.enabled ?? 0} / {health.schedules?.total ?? 0} active
                </Badge>
              }
              testId="card-schedules"
            >
              {(health.schedules?.total ?? 0) === 0 ? (
                <div className="text-xs text-slate-500 py-4 text-center">
                  No schedules configured yet. Create one from the Reports page → Schedules tab.
                </div>
              ) : (
                <div className="space-y-3">
                  <div>
                    <div className="text-xs font-semibold text-emerald-700 mb-1 flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Recent successes
                    </div>
                    {(health.schedules?.recentSuccess || []).length === 0 ? (
                      <div className="text-xs text-slate-400 pl-4">—</div>
                    ) : (
                      <ul className="space-y-1">
                        {health.schedules.recentSuccess.map((s, i) => (
                          <li key={`ok-${i}`} className="text-xs text-slate-700 pl-4 flex items-center justify-between gap-2" data-testid={`schedule-ok-${i}`}>
                            <span className="truncate">{s.name}</span>
                            <span className="text-slate-400 whitespace-nowrap">
                              {s.lastRunAt ? new Date(s.lastRunAt).toLocaleString() : '—'}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-red-700 mb-1 flex items-center gap-1">
                      <XCircle className="w-3.5 h-3.5" /> Recent errors
                    </div>
                    {(health.schedules?.recentErrors || []).length === 0 ? (
                      <div className="text-xs text-slate-400 pl-4">—</div>
                    ) : (
                      <ul className="space-y-1">
                        {health.schedules.recentErrors.map((s, i) => (
                          <li key={`err-${i}`} className="text-xs pl-4" data-testid={`schedule-err-${i}`}>
                            <div className="flex items-center justify-between gap-2">
                              <span className="truncate text-slate-700">{s.name}</span>
                              <span className="text-slate-400 whitespace-nowrap">
                                {s.lastRunAt ? new Date(s.lastRunAt).toLocaleString() : '—'}
                              </span>
                            </div>
                            <div className="text-red-600 truncate pl-2">{s.lastStatus}</div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </SectionCard>
          </div>
        )}

        {/* Weekly Backup Emails */}
        {backupCfg && (
          <Card className="mt-4 p-5" data-testid="card-backup">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Mail className="w-4 h-4 text-blue-700" />
                </div>
                <div>
                  <h2 className="font-semibold text-slate-800">Weekly Backup Emails</h2>
                  <p className="text-xs text-slate-500">A JSON snapshot of banks, vendors, companies and settings mailed to you every week.</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={downloadActivityLogsCsv} className="gap-1.5" data-testid="dl-activity-logs">
                  <Download className="w-3.5 h-3.5" /> Activity Logs CSV
                </Button>
                <Button size="sm" variant="outline" onClick={runBackupNow} disabled={backupSending || !(backupCfg.emails && backupCfg.emails.length)} className="gap-1.5" data-testid="send-backup-now">
                  <Mail className="w-3.5 h-3.5" />
                  {backupSending ? 'Sending…' : 'Send Backup Now'}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-slate-600 mb-1 block">Recipient emails (comma-separated)</label>
                <Input
                  value={backupEmailsInput}
                  onChange={(e) => setBackupEmailsInput(e.target.value)}
                  placeholder="you@company.com, backup@company.com"
                  data-testid="backup-emails-input"
                />
              </div>

              <div className="flex items-center gap-3">
                <label className="text-xs text-slate-600 flex-shrink-0">Weekly on</label>
                <select
                  value={backupCfg.dayOfWeek || 'monday'}
                  onChange={(e) => setBackupCfg({ ...backupCfg, dayOfWeek: e.target.value })}
                  className="h-9 px-2 border border-slate-200 rounded-md text-sm"
                  data-testid="backup-day-select"
                >
                  {['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].map((d) => (
                    <option key={d} value={d}>{d[0].toUpperCase() + d.slice(1)}</option>
                  ))}
                </select>
                <label className="text-xs text-slate-600 flex-shrink-0">at</label>
                <select
                  value={backupCfg.hourUtc ?? 6}
                  onChange={(e) => setBackupCfg({ ...backupCfg, hourUtc: Number(e.target.value) })}
                  className="h-9 px-2 border border-slate-200 rounded-md text-sm"
                  data-testid="backup-hour-select"
                >
                  {Array.from({ length: 24 }, (_, h) => (
                    <option key={h} value={h}>{String(h).padStart(2, '0')}:00 UTC</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap items-center gap-4 text-sm">
              {['includeBanks', 'includeVendors', 'includeCompanies', 'includeSettings'].map((key) => (
                <label key={key} className="flex items-center gap-1.5 cursor-pointer" data-testid={`backup-${key}`}>
                  <input
                    type="checkbox"
                    checked={backupCfg[key] !== false}
                    onChange={(e) => setBackupCfg({ ...backupCfg, [key]: e.target.checked })}
                    className="h-4 w-4 rounded border-slate-300"
                  />
                  <span className="text-xs text-slate-700">{key.replace('include', '')}</span>
                </label>
              ))}
            </div>

            <div className="mt-4 flex items-center justify-between flex-wrap gap-3 pt-3 border-t border-slate-100">
              <label className="flex items-center gap-2 cursor-pointer" data-testid="backup-enabled">
                <input
                  type="checkbox"
                  checked={!!backupCfg.enabled}
                  onChange={(e) => setBackupCfg({ ...backupCfg, enabled: e.target.checked })}
                  className="h-4 w-4 rounded border-slate-300"
                />
                <span className="text-sm font-semibold text-slate-800">Enable scheduled weekly backup</span>
              </label>
              <div className="flex items-center gap-2">
                <div className="text-xs text-slate-500">
                  {backupCfg.lastRunAt && (
                    <>Last run: {new Date(backupCfg.lastRunAt).toLocaleString()} ·{' '}
                      <span className={backupCfg.lastStatus === 'OK' ? 'text-emerald-700' : 'text-red-600'}>
                        {backupCfg.lastStatus || '—'}
                      </span>
                    </>
                  )}
                </div>
                <Button size="sm" onClick={saveBackupCfg} disabled={backupSaving} className="gap-1.5" data-testid="backup-save">
                  <Save className="w-3.5 h-3.5" />
                  {backupSaving ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Super Admin management */}
        {superAdmins.length > 0 && (
          <Card className="mt-4 p-5" data-testid="card-superadmins">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                  <ShieldAlert className="w-4 h-4 text-amber-700" />
                </div>
                <div>
                  <h2 className="font-semibold text-slate-800">Super Admin Accounts</h2>
                  <p className="text-xs text-slate-500">Rotate passwords, remove stale accounts. Reset affects login immediately.</p>
                </div>
              </div>
              <Badge className="bg-amber-100 text-amber-800 border-amber-200">{superAdmins.length}</Badge>
            </div>
            <div className="space-y-2">
              {superAdmins.map((u) => (
                <div
                  key={u.id}
                  className="flex items-center justify-between gap-3 py-2 px-3 bg-slate-50 rounded-lg border border-slate-200"
                  data-testid={`sa-row-${u.username}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-slate-800 truncate flex items-center gap-2">
                      {u.username}
                      <span className="text-xs text-slate-400 font-normal">· {u.name}</span>
                    </div>
                    <div className="text-xs text-slate-500 flex items-center gap-3 mt-0.5 flex-wrap">
                      <span>id: <code>{u.id.slice(0, 8)}…</code></span>
                      <span>created: {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1"
                      onClick={() => openResetDialog(u)}
                      data-testid={`sa-reset-${u.username}`}
                    >
                      <KeyRound className="w-3.5 h-3.5" /> Reset Password
                    </Button>
                    {u.id !== me?.id && superAdmins.length > 1 && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1 text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => openDeleteDialog(u)}
                        data-testid={`sa-delete-${u.username}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-3">
              Resetting or deleting a Super Admin needs your password, delete PIN and a code emailed to the security address.
            </p>
          </Card>
        )}

        <div className="text-xs text-slate-400 mt-6 flex items-center gap-2 justify-center">
          <Clock className="w-3 h-3" />
          Endpoint: <code>GET /api/system/health</code> · Super Admin only
        </div>
      </div>

      {/* Reset password dialog */}
      <StepUpDialog
        open={resetDialog.open}
        onOpenChange={(open) => !open && setResetDialog({ open: false, user: null, newPwd: '', confirmPwd: '', busy: false })}
        action="reset_superadmin_password"
        targetId={resetDialog.user?.id || ''}
        targetLabel={resetDialog.user?.username || ''}
        title="Reset Super Admin Password"
        description={<>New password for <strong>{resetDialog.user?.username}</strong> (min 10 characters). All of that account's sessions will be signed out.</>}
        extraReady={resetDialog.newPwd.length >= 10 && resetDialog.newPwd === resetDialog.confirmPwd}
        confirmLabel="Reset Password"
        onConfirm={submitReset}
        testIdPrefix="sa-reset"
      >
        <Input type="password" autoComplete="new-password" placeholder="New password" value={resetDialog.newPwd}
          onChange={(e) => setResetDialog((d) => ({ ...d, newPwd: e.target.value }))} data-testid="sa-reset-new-pwd" />
        <Input type="password" autoComplete="new-password" placeholder="Confirm new password" value={resetDialog.confirmPwd}
          onChange={(e) => setResetDialog((d) => ({ ...d, confirmPwd: e.target.value }))} data-testid="sa-reset-confirm-pwd" />
      </StepUpDialog>

      {/* Delete super admin dialog */}
      <StepUpDialog
        open={deleteDialog.open}
        onOpenChange={(open) => !open && setDeleteDialog({ open: false, user: null, busy: false })}
        action="delete_superadmin"
        targetId={deleteDialog.user?.id || ''}
        targetLabel={deleteDialog.user?.username || ''}
        title="Delete Super Admin Account"
        description={<>Permanently removes <strong>{deleteDialog.user?.username}</strong> (id {deleteDialog.user?.id?.slice(0, 8)}…). You cannot delete yourself or the last Super Admin.</>}
        confirmLabel="Delete Super Admin"
        onConfirm={submitDelete}
        testIdPrefix="sa-delete"
      />
    </div>
  );
};

export default SystemHealthPage;
